import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  MessageSquare,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  interviewInvitationsApi,
  InterviewInvitationWithCandidate,
  InterviewInvitationStatus,
} from '../services/interviewInvitationsApi';
import './Pasiulymai.scss';

/**
 * "Pokalbiai su kandidatais" — employer-facing list of interview
 * invitations they've sent. Same visual language as the candidate-side
 * Pasiūlymai page so both ends of the conversation feel like one feature.
 */

type TabKey = 'all' | 'pending' | 'accepted' | 'archived';

const STATUS_LABEL: Record<InterviewInvitationStatus, string> = {
  pending: 'Laukia patvirtinimo',
  accepted: 'Aktyvus',
  declined: 'Atmestas',
  withdrawn: 'Atšauktas',
};

function formatDateTime(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('lt-LT');
}

function candidateDisplay(row: InterviewInvitationWithCandidate): string {
  const personal = `${row.candidate_first_name ?? ''} ${row.candidate_last_name ?? ''}`.trim();
  if (personal) return personal;
  return row.candidate_email ?? 'Kandidatas';
}

const EmployerPokalbiai: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const [invitations, setInvitations] = useState<InterviewInvitationWithCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);
  const [tab, setTab] = useState<TabKey>('all');

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const { invitations } = await interviewInvitationsApi.myOutgoing();
      setInvitations(invitations);
      setError(null);
      window.dispatchEvent(new CustomEvent('interview-invitations-changed'));
    } catch (err: any) {
      setError(err?.message || 'Nepavyko įkelti pokalbių');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    refresh();
  }, [isAuthenticated, refresh]);

  const counts = useMemo(() => {
    return {
      all: invitations.length,
      pending: invitations.filter((i) => i.status === 'pending').length,
      accepted: invitations.filter((i) => i.status === 'accepted').length,
      archived: invitations.filter(
        (i) => i.status === 'declined' || i.status === 'withdrawn',
      ).length,
    };
  }, [invitations]);

  const filtered = useMemo(() => {
    switch (tab) {
      case 'pending':
        return invitations.filter((i) => i.status === 'pending');
      case 'accepted':
        return invitations.filter((i) => i.status === 'accepted');
      case 'archived':
        return invitations.filter(
          (i) => i.status === 'declined' || i.status === 'withdrawn',
        );
      default:
        return invitations;
    }
  }, [invitations, tab]);

  if (isLoading) return <div className="invitations-page-loading">Kraunama...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.user_type !== 'employer') return <Navigate to="/" replace />;

  const handleWithdraw = async (id: number) => {
    setActingId(id);
    try {
      await interviewInvitationsApi.withdraw(id);
      showToast('Kvietimas atšauktas', 'success');
      await refresh();
    } catch (err: any) {
      showToast(err?.message || 'Nepavyko atšaukti kvietimo', 'error');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="invitations-page">
      <div className="invitations-page-header">
        <div className="invitations-page-title">
          <MessageSquare size={22} />
          <h1>Pokalbiai su kandidatais</h1>
        </div>
      </div>
      <p className="invitations-page-subtitle">
        Visi kvietimai pokalbiui, kuriuos išsiuntėte iš kandidatų CV.
      </p>

      {error && (
        <div className="invitations-alert invitations-alert--error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {invitations.length === 0 && !loading ? (
        <EmptyState />
      ) : (
        <>
          <div className="invitations-tabs">
            <Tab
              label="Visi"
              count={counts.all}
              active={tab === 'all'}
              onClick={() => setTab('all')}
            />
            <Tab
              label="Laukia patvirtinimo"
              count={counts.pending}
              active={tab === 'pending'}
              onClick={() => setTab('pending')}
            />
            <Tab
              label="Aktyvūs"
              count={counts.accepted}
              active={tab === 'accepted'}
              onClick={() => setTab('accepted')}
            />
            <Tab
              label="Archyvas"
              count={counts.archived}
              active={tab === 'archived'}
              onClick={() => setTab('archived')}
            />
          </div>

          {loading ? (
            <div className="invitations-page-loading">Kraunama...</div>
          ) : filtered.length === 0 ? (
            <p className="invitations-empty-hint">Šiuo statusu pokalbių nėra.</p>
          ) : (
            <div className="invitations-grid">
              {filtered.map((inv) => (
                <EmployerInvitationCard
                  key={inv.id}
                  invitation={inv}
                  acting={actingId === inv.id}
                  onWithdraw={() => handleWithdraw(inv.id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Tab: React.FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => (
  <button
    type="button"
    className={`invitations-tab ${active ? 'is-active' : ''}`}
    onClick={onClick}
  >
    {label} <span className="invitations-tab-count">{count}</span>
  </button>
);

const EmployerInvitationCard: React.FC<{
  invitation: InterviewInvitationWithCandidate;
  acting: boolean;
  onWithdraw: () => void;
}> = ({ invitation, acting, onWithdraw }) => {
  const isAccepted = invitation.status === 'accepted';
  const isPending = invitation.status === 'pending';
  const isClosed =
    invitation.status === 'declined' || invitation.status === 'withdrawn';

  const cardClass = [
    'invitation-card',
    isAccepted ? 'invitation-card--clickable' : '',
    isClosed ? 'invitation-card--closed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <div className="invitation-card-top">
        <span className={`invitation-status invitation-status--${invitation.status}`}>
          {STATUS_LABEL[invitation.status]}
        </span>
        {invitation.unread_count > 0 && (
          <span
            className="invitation-unread-pill"
            title={`${invitation.unread_count} neperskaitytos žinutės`}
          >
            <MessageSquare size={11} />
            {invitation.unread_count}
          </span>
        )}
      </div>

      <h3 className="invitation-name">{candidateDisplay(invitation)}</h3>
      <span className="invitation-meta">
        <Clock size={11} />
        {isPending
          ? `Išsiųsta ${formatDateTime(invitation.created_at)}`
          : `Atnaujinta ${formatDateTime(invitation.updated_at)}`}
      </span>

      {invitation.intro_message && (
        <p className="invitation-intro">{invitation.intro_message}</p>
      )}

      {isPending && (
        <div className="invitation-actions">
          <button
            type="button"
            className="invitation-btn invitation-btn--secondary"
            onClick={onWithdraw}
            disabled={acting}
          >
            Atšaukti
          </button>
        </div>
      )}

      {isAccepted && (
        <span className="invitation-card-cta">
          <MessageSquare size={14} /> Atidaryti pokalbį
        </span>
      )}
    </>
  );

  return isAccepted ? (
    <Link to={`/employer/pokalbiai/${invitation.id}`} className={cardClass}>
      {inner}
    </Link>
  ) : (
    <article className={cardClass}>{inner}</article>
  );
};

const EmptyState: React.FC = () => (
  <div className="invitations-empty-state">
    <div className="invitations-empty-icon">
      <MessageSquare size={42} />
    </div>
    <h2>Kol kas nesate išsiuntę kvietimų</h2>
    <p>
      Atrasdami tinkamą kandidatą CV duomenų bazėje, atidarykite jo CV ir
      spauskite „Pakviesti pokalbiui".
    </p>
  </div>
);

export default EmployerPokalbiai;
